import {
  supporterCountriesQuery,
  supporterSettingsQuery,
  useDeleteSupporterSettingMutation,
  useSetSupporterSettingMutation,
} from "@/api/supporter";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { tokenCurrencyToMoneyCurrency } from "@/modules/money-fns";
import { Box, Flex, Input, Select, Text, toast } from "@givenwell/components";
import { PencilIcon } from "@givenwell/core-solid-icons";
import { colors } from "@givenwell/theme";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ReactNode, useMemo, useState } from "react";

export const Route = createFileRoute("/admin/supporters/$supporterId/settings")({
  component: SettingsPage,
  beforeLoad: () => ({
    breadcrumb: "Settings",
  }),
});

type SettingType = "boolean" | "date" | "eapClaimFee";

type KnownSetting = {
  key: string;
  label: string;
  type: SettingType;
  description?: string;
  defaultValue?: any;
};

// Mirror of SupporterSettingKeys on the server
// (Givenwell.Domains/Domains/Supporter/SupporterSettingsRepository.cs).
const KNOWN_SETTINGS: KnownSetting[] = [
  {
    key: "requireWorkEmail",
    label: "Require work email",
    type: "boolean",
    description: "Members must sign up using their work email address.",
    defaultValue: false,
  },
  {
    key: "returnLeaverTokens",
    label: "Return leaver tokens",
    type: "boolean",
    description: "Unspent tokens are returned to the supporter when a member leaves.",
    defaultValue: false,
  },
  {
    key: "eapClaimFee",
    label: "EAP claim fee",
    type: "eapClaimFee",
    description:
      "Per-supporter overrides for the fee charged on EAP claims, in whole currency units. Only the supporter's registered currencies are shown; leave a currency blank to fall back to the platform default.",
  },
  {
    key: "oldestClaimDate",
    label: "Oldest claim date",
    type: "date",
    description:
      "When set, members of this supporter can submit claims back to this date, overriding the normal join-date and 12-month look-back floor.",
  },
  {
    key: "consolidatedEapBilling",
    label: "Consolidated EAP billing",
    type: "boolean",
    description:
      "When true, the supporter is billed for all their EAP arrears (sessions + claims) in their country's currency.",
    defaultValue: false,
  },
];

const BOOLEAN_KEYS = KNOWN_SETTINGS.filter(s => s.type === "boolean").map(s => s.key);
const EAP_CLAIM_FEE_KEY = "eapClaimFee";
const OLDEST_CLAIM_DATE_KEY = "oldestClaimDate";

type BoolDraft = "unset" | "true" | "false";
type Operation = { key: string; action: "set"; value: unknown } | { key: string; action: "delete" };

function SettingsPage() {
  const { supporterId } = Route.useParams();
  const { data: settings } = useSuspenseQuery(supporterSettingsQuery(supporterId));
  const { data: countries } = useSuspenseQuery(supporterCountriesQuery(supporterId));

  const currencies = useMemo(() => {
    const set = new Set<string>();
    for (const c of countries.countries) {
      const currency = tokenCurrencyToMoneyCurrency(c.token_currency);
      if (currency) set.add(currency);
    }
    return [...set].sort();
  }, [countries]);

  const setMutation = useSetSupporterSettingMutation(supporterId);
  const deleteMutation = useDeleteSupporterSettingMutation(supporterId);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [boolDrafts, setBoolDrafts] = useState<Record<string, BoolDraft>>({});
  const [feeDrafts, setFeeDrafts] = useState<Record<string, string>>({});
  const [dateDraft, setDateDraft] = useState("");

  const startEditing = () => {
    setBoolDrafts(seedBoolDrafts(settings));
    setFeeDrafts(seedFeeDrafts(settings, currencies));
    setDateDraft(seedDateDraft(settings));
    setEditing(true);
  };

  const operations = useMemo(
    () => (editing ? computeOperations(settings, currencies, boolDrafts, feeDrafts, dateDraft) : []),
    [editing, settings, currencies, boolDrafts, feeDrafts, dateDraft],
  );

  const save = async () => {
    setSaving(true);
    try {
      for (const op of operations) {
        if (op.action === "delete") {
          await deleteMutation.mutateAsync({ key: op.key });
        } else {
          await setMutation.mutateAsync({ key: op.key, value: op.value });
        }
      }
      if (operations.length > 0) toast.success("Settings saved");
      setEditing(false);
    } catch {
      // Per-mutation error toasts are shown by the mutation hooks.
    } finally {
      setSaving(false);
    }
  };

  const knownKeys = new Set(KNOWN_SETTINGS.map(s => s.key));
  const unknownEntries = Object.entries(settings).filter(([key]) => !knownKeys.has(key));

  return (
    <Box css={{ spaceY: 16 }}>
      <Flex css={{ flexDir: "row", gap: 16, flexShrink: 0 }}>
        <Card
          title="Settings"
          description="Per-supporter feature flags."
          css={{ width: 700 }}
          actions={
            editing ? (
              <Flex css={{ gap: 8 }}>
                <Button variant="plain" color="neutral" size="sm" disabled={saving} onClick={() => setEditing(false)}>
                  Cancel
                </Button>
                <Button
                  variant="filled"
                  color="primary"
                  size="sm"
                  loading={saving}
                  disabled={operations.length === 0}
                  onClick={save}
                >
                  Save
                </Button>
              </Flex>
            ) : (
              <Button variant="tonal" color="primary" size="sm" leftIcon={<PencilIcon />} onClick={startEditing}>
                Edit
              </Button>
            )
          }
        >
          <Flex css={{ flexDir: "column", gap: 16 }}>
            {KNOWN_SETTINGS.map(setting => (
              <SettingRow key={setting.key} label={setting.label} description={setting.description}>
                {editing ? (
                  <EditValue
                    setting={setting}
                    currencies={currencies}
                    saving={saving}
                    boolDrafts={boolDrafts}
                    setBoolDrafts={setBoolDrafts}
                    feeDrafts={feeDrafts}
                    setFeeDrafts={setFeeDrafts}
                    dateDraft={dateDraft}
                    setDateDraft={setDateDraft}
                  />
                ) : (
                  <DisplayValue setting={setting} value={settings[setting.key]} />
                )}
              </SettingRow>
            ))}
          </Flex>

          {unknownEntries.length > 0 && (
            <Box css={{ pt: 24 }}>
              <Text css={{ fontScale: "sm", weight: 500, color: colors.text2, pb: 8, display: "block" }}>
                Other settings
              </Text>
              <Flex css={{ flexDir: "column", gap: 8 }}>
                {unknownEntries.map(([key, value]) => (
                  <Flex key={key} css={{ justifyContent: "space-between", alignItems: "center", gap: 16 }}>
                    <Text css={{ fontScale: "sm" }}>{key}</Text>
                    <Text css={{ fontScale: "sm", color: colors.text2, fontFamily: "monospace" }}>
                      {JSON.stringify(value)}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            </Box>
          )}
        </Card>
      </Flex>
    </Box>
  );
}

function SettingRow({ label, description, children }: { label: string; description?: string; children: ReactNode }) {
  return (
    <Flex css={{ justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
      <Flex css={{ flexDir: "column", gap: 2, flex: 1 }}>
        <Text css={{ fontScale: "sm", weight: 500 }}>{label}</Text>
        {description && <Text css={{ fontScale: "xs", color: colors.text2 }}>{description}</Text>}
      </Flex>
      <Box css={{ flexShrink: 0, textAlign: "right" }}>{children}</Box>
    </Flex>
  );
}

// Format a setting's default using the same presentation as its value (e.g. booleans as
// "Yes"/"No" to match the display and edit controls).
function formatDefaultLabel(setting: KnownSetting): string | undefined {
  if (setting.defaultValue === undefined) return undefined;
  if (setting.type === "boolean") return setting.defaultValue ? "Yes" : "No";
  return JSON.stringify(setting.defaultValue);
}

function DisplayValue({ setting, value }: { setting: KnownSetting; value: unknown }) {
  const defaultLabel = formatDefaultLabel(setting);
  const notSet = (
    <Text css={{ fontScale: "xs", color: colors.text2, fontStyle: "italic" }}>
      Not set{defaultLabel !== undefined && ` (default: ${defaultLabel})`}
    </Text>
  );

  if (setting.type === "boolean") {
    if (typeof value !== "boolean") return notSet;
    return <Text css={{ fontScale: "sm", weight: 500 }}>{value ? "Yes" : "No"}</Text>;
  }

  if (setting.type === "date") {
    if (typeof value !== "string" || !value) return notSet;
    return <Text css={{ fontScale: "sm", fontFamily: "monospace" }}>{value}</Text>;
  }

  // eapClaimFee
  const fees = value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
  const entries = Object.entries(fees);
  if (entries.length === 0) return notSet;
  return (
    <Flex css={{ flexDir: "column", gap: 2, alignItems: "flex-end" }}>
      {entries.map(([cur, amount]) => (
        <Text key={cur} css={{ fontScale: "sm", fontFamily: "monospace" }}>
          {cur} {String(amount)}
        </Text>
      ))}
    </Flex>
  );
}

function EditValue({
  setting,
  currencies,
  saving,
  boolDrafts,
  setBoolDrafts,
  feeDrafts,
  setFeeDrafts,
  dateDraft,
  setDateDraft,
}: {
  setting: KnownSetting;
  currencies: string[];
  saving: boolean;
  boolDrafts: Record<string, BoolDraft>;
  setBoolDrafts: React.Dispatch<React.SetStateAction<Record<string, BoolDraft>>>;
  feeDrafts: Record<string, string>;
  setFeeDrafts: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  dateDraft: string;
  setDateDraft: (value: string) => void;
}) {
  if (setting.type === "boolean") {
    return (
      <Select
        size="sm"
        value={boolDrafts[setting.key] ?? "unset"}
        disabled={saving}
        css={{ width: 130 }}
        onValueChange={next => setBoolDrafts(prev => ({ ...prev, [setting.key]: next as BoolDraft }))}
      >
        <option value="unset">Not set</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </Select>
    );
  }

  if (setting.type === "date") {
    return (
      <Input
        type="date"
        size="sm"
        value={dateDraft}
        disabled={saving}
        onValueChange={setDateDraft}
        css={{ width: 170 }}
      />
    );
  }

  // eapClaimFee
  if (currencies.length === 0) {
    return <Text css={{ fontScale: "xs", color: colors.text2, fontStyle: "italic" }}>No registered countries</Text>;
  }
  return (
    <Flex css={{ flexDir: "column", gap: 8, alignItems: "flex-end" }}>
      {currencies.map(cur => (
        <Flex key={cur} css={{ gap: 8, alignItems: "center" }}>
          <Text css={{ fontScale: "sm", color: colors.text2, width: 40, textAlign: "right" }}>{cur}</Text>
          <Input
            type="number"
            size="sm"
            inputMode="decimal"
            placeholder="default"
            value={feeDrafts[cur] ?? ""}
            disabled={saving}
            onValueChange={next => setFeeDrafts(prev => ({ ...prev, [cur]: next }))}
            css={{ width: 110 }}
          />
        </Flex>
      ))}
    </Flex>
  );
}

// -------------------------------------------------------------------------------------------------
// Draft seeding + diffing
// -------------------------------------------------------------------------------------------------

function seedBoolDrafts(settings: Record<string, unknown>): Record<string, BoolDraft> {
  return Object.fromEntries(
    BOOLEAN_KEYS.map(key => {
      const value = settings[key];
      const draft: BoolDraft = typeof value === "boolean" ? (value ? "true" : "false") : "unset";
      return [key, draft];
    }),
  );
}

function seedFeeDrafts(settings: Record<string, unknown>, currencies: string[]): Record<string, string> {
  const value = settings[EAP_CLAIM_FEE_KEY];
  const fees = value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
  return Object.fromEntries(
    currencies.map(cur => {
      const fee = fees[cur];
      return [cur, typeof fee === "number" || typeof fee === "string" ? String(fee) : ""];
    }),
  );
}

function seedDateDraft(settings: Record<string, unknown>): string {
  const value = settings[OLDEST_CLAIM_DATE_KEY];
  return typeof value === "string" ? value : "";
}

function computeOperations(
  settings: Record<string, unknown>,
  currencies: string[],
  boolDrafts: Record<string, BoolDraft>,
  feeDrafts: Record<string, string>,
  dateDraft: string,
): Operation[] {
  const operations: Operation[] = [];

  // Booleans.
  for (const key of BOOLEAN_KEYS) {
    const original = typeof settings[key] === "boolean" ? settings[key] : undefined;
    const draft = boolDrafts[key] ?? "unset";
    const desired = draft === "unset" ? undefined : draft === "true";
    if (desired === original) continue;
    operations.push(desired === undefined ? { key, action: "delete" } : { key, action: "set", value: desired });
  }

  // Oldest claim date.
  {
    const original = typeof settings[OLDEST_CLAIM_DATE_KEY] === "string" ? settings[OLDEST_CLAIM_DATE_KEY] : undefined;
    const desired = dateDraft || undefined;
    if (desired !== original) {
      operations.push(
        desired === undefined
          ? { key: OLDEST_CLAIM_DATE_KEY, action: "delete" }
          : { key: OLDEST_CLAIM_DATE_KEY, action: "set", value: desired },
      );
    }
  }

  // EAP claim fee.
  {
    const rawOriginal = settings[EAP_CLAIM_FEE_KEY];
    const originalFees =
      rawOriginal && typeof rawOriginal === "object" && !Array.isArray(rawOriginal)
        ? (rawOriginal as Record<string, unknown>)
        : {};
    const originalKey = JSON.stringify(normalizeFees(originalFees, currencies));

    const desiredFees: Record<string, number> = {};
    for (const cur of currencies) {
      const raw = feeDrafts[cur]?.trim();
      if (raw === undefined || raw === "") continue;
      const num = Number(raw);
      if (!Number.isFinite(num)) continue;
      desiredFees[cur] = num;
    }
    const desiredKey = JSON.stringify(desiredFees);

    if (desiredKey !== originalKey) {
      operations.push(
        Object.keys(desiredFees).length === 0
          ? { key: EAP_CLAIM_FEE_KEY, action: "delete" }
          : { key: EAP_CLAIM_FEE_KEY, action: "set", value: desiredFees },
      );
    }
  }

  return operations;
}

// Normalise the stored fee dict to a comparable shape: numeric values, only registered
// currencies, in a stable key order.
function normalizeFees(fees: Record<string, unknown>, currencies: string[]): Record<string, number> {
  const result: Record<string, number> = {};
  for (const cur of currencies) {
    const value = fees[cur];
    if (typeof value === "number" && Number.isFinite(value)) {
      result[cur] = value;
    }
  }
  return result;
}
